//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { Pagination } from "../../services/stores/types";

//*type for Student data
type Organizations = {
  id: string;
  name: string;
  responsibleId: string;
  logoPath: string;
  description: string;
};

type OrganizationsDetails = {
  id: string;
  name: string;
  logoPath: string | null;
  description: string;
  responsibleId: string;
  responsible: {
    id: string;
    name: string;
    email: string;
    role: {
      slug: string;
      name: string;
    };
  };
  createdAt: string;
  updatedAt: string;
};

//*type for the structure of Admin API responses
type Admin = {
  id: string;
  idUser: string;
  person: {
    id: string;
    name: string;
    email: string;
  };
  organization: {
    id: string;
    name: string;
  };
  role: string;
  createdAt: string;
  updatedAt: string;
};

//*type for the structure of Student API responses
type OrganizationsResponse = {
  organizations: Organizations[];
  pagination: Pagination;
};

// API methods for student operations
export const organizationAPI = {
  // Fetch all students with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<OrganizationsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/organizations?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          organizations: data.organizations || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        throw new Error("Failed to fetch students");
      });
  },

  getById: async function (
    id: string,
    token: string
  ): Promise<OrganizationsDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/organizations/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching student:", error);
        throw new Error("Failed to fetch student");
      });
  },
  // Create a new student
  create: async function (
    data: {
      name: string;
      description: string;
      logoPath: File | null;
      adminName: string;
      adminEmail: string;
    },
    token: string
  ): Promise<Organizations> {
    // Criando FormData para envio de arquivo e outros dados
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("description", data.description);
    formData.append("admin_name", data.adminName);
    formData.append("admin_email", data.adminEmail);

    // Adiciona o logoPath se existir
    if (data.logoPath) {
      formData.append("logoPath", data.logoPath);
    }

    return api
      .request({
        url: `${API_URI}/admin/private/organizations`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          console.error("Error creating organization:", error.response.data);
        } else {
          console.error("Error creating organization:", error);
        }
        throw new Error("Failed to create organization");
      });
  },

  update: async function (
    id: string,
    data: {
      name: string;
      description: string;
      logoPath: File | null;
      responsibleId: string;
    },
    token: string
  ): Promise<Organizations> {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("description", data.description);
    formData.append("responsibleId", data.responsibleId);

    if (data.logoPath) {
      formData.append("logoPath", data.logoPath);
    }

    return api
      .request({
        url: `${API_URI}/admin/private/organizations/${id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          console.error("Error creating organization:", error.response.data);
        } else {
          console.error("Error creating organization:", error);
        }
        throw new Error("Failed to create organization");
      });
  },

  // Delete a student by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/organizations/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {})
      .catch((error) => {
        console.error("Error deleting student:", error);
        throw new Error("Failed to delete student");
      });
  },

  getAdminsByOrganization: async function (
    id: string,
    token: string
  ): Promise<Admin[]> {
    return api
      .request({
        url: `${API_URI}/admin/private/organizations/${id}/admins`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        return data.admins || [];
      })
      .catch((error) => {
        console.error("Error fetching admins:", error);
        throw new Error("Failed to fetch admins");
      });
  },
};
